import { useState, useCallback, useEffect } from 'react';

export interface CachedPrompt {
  id: string;
  text: string;
  timestamp: number;
}

const CACHE_KEY = 'legal-research-prompt-cache';
const MAX_CACHE_SIZE = 9;

export const usePromptCache = () => {
  const [cachedPrompts, setCachedPrompts] = useState<CachedPrompt[]>([]);

  // Load cache from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as CachedPrompt[];
        setCachedPrompts(parsed);
      }
    } catch (error) {
      console.error('Failed to load prompt cache:', error);
    }
  }, []);

  // Save cache to localStorage whenever it changes
  const saveCache = useCallback((prompts: CachedPrompt[]) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(prompts));
      setCachedPrompts(prompts);
    } catch (error) {
      console.error('Failed to save prompt cache:', error);
    }
  }, []);

  // Add prompt to cache
  const addToCache = useCallback((text: string) => {
    const newPrompt: CachedPrompt = {
      id: crypto.randomUUID(),
      text: text.trim(),
      timestamp: Date.now()
    };

    setCachedPrompts(current => {
      // Check if prompt already exists
      if (current.some(p => p.text === newPrompt.text)) {
        return current;
      }

      // Add new prompt and maintain max size (FIFO)
      const updated = [newPrompt, ...current];
      const trimmed = updated.slice(0, MAX_CACHE_SIZE);
      
      saveCache(trimmed);
      return trimmed;
    });
  }, [saveCache]);

  // Remove prompt from cache
  const removeFromCache = useCallback((id: string) => {
    setCachedPrompts(current => {
      const updated = current.filter(p => p.id !== id);
      saveCache(updated);
      return updated;
    });
  }, [saveCache]);

  // Check if prompt is cached
  const isPromptCached = useCallback((text: string) => {
    return cachedPrompts.some(p => p.text === text.trim());
  }, [cachedPrompts]);

  // Clear all cache
  const clearCache = useCallback(() => {
    try {
      localStorage.removeItem(CACHE_KEY);
      setCachedPrompts([]);
    } catch (error) {
      console.error('Failed to clear prompt cache:', error);
    }
  }, []);

  return {
    cachedPrompts,
    addToCache,
    removeFromCache,
    isPromptCached,
    clearCache
  };
};